import { create } from 'zustand';
import { createEntityStore } from '@redpanda/utils';
import { bookingApi } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';

export interface Booking {
  id: string;
  customerId: string;
  storeId: string;
  serviceId: string;
  staffId?: string | null;
  date: string;
  price: number;
  duration: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  managementToken: string;
  note?: string | null;
  customer: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
  };
  service: {
    id: string;
    name: string;
    description?: string | null;
    price: number;
    duration: number;
    category?: string | null;
  };
  staff?: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
  } | null;
}

// 創建基礎的預約 store (使用 createEntityStore 獲得分頁功能)
export const useBookingStoreBase = createEntityStore<Booking>({
  entityName: 'booking',
  fetchFn: (page: number) => bookingApi.getPaginated(page, 20),
  deleteFn: (id: string) => bookingApi.delete(id),
  toast,
  messages: {
    deleteSuccess: '預約已刪除',
    deleteError: '刪除預約失敗',
    fetchError: '取得預約失敗',
  },
});

// 擴展 store 以添加預約特有的功能
interface ExtendedBookingState {
  // 取消預約相關
  showCancelConfirmModal: boolean;
  bookingToCancelId: string | null;
  handleCancel: (id: string) => void;
  confirmCancel: () => Promise<void>;
  closeCancelModal: () => void;

  // 更新預約
  isUpdating: boolean;
  updateBooking: (id: string, data: any) => Promise<void>;

  // 創建預約
  isCreating: boolean;
  createBooking: (storeId: string, data: any) => Promise<void>;

  // 篩選相關
  filters: {
    storeId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  };
  setFilters: (filters: any) => void;
}

export const useBookingStore = create<ExtendedBookingState>((set, get) => ({
  // 取消預約相關
  showCancelConfirmModal: false,
  bookingToCancelId: null,

  handleCancel: (id: string) => {
    set({ bookingToCancelId: id, showCancelConfirmModal: true });
  },

  confirmCancel: async () => {
    const { bookingToCancelId } = get();
    if (!bookingToCancelId) return;

    // 樂觀更新：立即關閉 Modal + 更新預約狀態
    const baseStore = useBookingStoreBase.getState();
    const originalItems = [...baseStore.items];

    baseStore.updateItem(bookingToCancelId, { status: 'CANCELLED' as const });
    set({
      showCancelConfirmModal: false,
      bookingToCancelId: null,
    });
    toast.success(i18n.t('common.messages.updateSuccess'));

    try {
      await bookingApi.cancel(bookingToCancelId);
    } catch (error: any) {
      // 失敗回滾
      useBookingStoreBase.setState({ items: originalItems });
      console.error('Failed to cancel booking:', error);
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
    }
  },

  closeCancelModal: () => {
    set({ showCancelConfirmModal: false, bookingToCancelId: null });
  },

  // 更新預約
  isUpdating: false,
  updateBooking: async (id: string, data: any) => {
    set({ isUpdating: true });

    // 樂觀更新：保存原始狀態並立即更新 UI
    const baseStore = useBookingStoreBase.getState();
    const originalItems = [...baseStore.items];

    // 立即更新 UI（樂觀更新）
    baseStore.updateItem(id, data);

    try {
      const response = await bookingApi.update(id, data);
      const updatedBooking = response.data;

      // API 成功後，用完整資料更新 store
      baseStore.updateItem(id, updatedBooking);

      set({ isUpdating: false });
      toast.success(i18n.t('common.messages.updateSuccess'));
    } catch (error: any) {
      console.error('Failed to update booking:', error);
      // 失敗時回滾到原始狀態
      useBookingStoreBase.setState({ items: originalItems });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
      set({ isUpdating: false });
      throw error;
    }
  },

  // 創建預約
  isCreating: false,
  createBooking: async (storeId: string, data: any) => {
    set({ isCreating: true });
    try {
      const bookingData = {
        ...data,
        storeId,
      };
      const response = await bookingApi.create(bookingData);
      const newBooking = response.data;

      // 添加到 store 中
      const baseStore = useBookingStoreBase.getState();
      baseStore.addItem(newBooking);

      set({ isCreating: false });
      toast.success(i18n.t('common.messages.createSuccess'));
    } catch (error: any) {
      console.error('Failed to create booking:', error);
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.createFailed');
      toast.error(errorMsg);
      set({ isCreating: false });
      throw error;
    }
  },

  // 篩選相關
  filters: {},
  setFilters: (filters: any) => {
    set({ filters });
  },
}));

// 提供統一的 hook 來訪問所有功能
export const useBookings = () => {
  const baseStore = useBookingStoreBase();
  const extendedStore = useBookingStore();

  return {
    // 基礎的分頁功能 (來自 createEntityStore)
    bookings: baseStore.items,
    pagination: baseStore.pagination,
    currentPage: baseStore.currentPage,
    error: baseStore.error,
    isLoading: baseStore.isLoading,
    isPageLoading: baseStore.isPageLoading,
    fetchBookings: baseStore.fetchItems,
    setCurrentPage: baseStore.setCurrentPage,
    refreshData: baseStore.refreshData,

    // 刪除功能 (來自 createEntityStore)
    showDeleteConfirmModal: baseStore.showDeleteConfirmModal,
    isDeleting: baseStore.isDeleting,
    handleDelete: baseStore.handleDelete,
    confirmDelete: baseStore.confirmDelete,
    closeDeleteModal: baseStore.closeDeleteModal,

    // 取消預約功能 (擴展功能)
    showCancelConfirmModal: extendedStore.showCancelConfirmModal,
    handleCancel: extendedStore.handleCancel,
    confirmCancel: extendedStore.confirmCancel,
    closeCancelModal: extendedStore.closeCancelModal,

    // CRUD 功能 (擴展功能)
    isCreating: extendedStore.isCreating,
    isUpdating: extendedStore.isUpdating,
    createBooking: extendedStore.createBooking,
    updateBooking: extendedStore.updateBooking,

    // 篩選功能
    filters: extendedStore.filters,
    setFilters: extendedStore.setFilters,
  };
};
